/**
 * SDL v2.0 parsing, validation and price ceiling — Phase C of REWRITE-PLAN.
 *
 * Why validate here rather than let the chain do it: the chain validates the
 * *deployment groups*, not the SDL, and by then the tenant has already paid gas
 * and the error names a group index rather than the service they mistyped. Most
 * real SDL mistakes are cross-references — a `deployment` entry naming a service
 * that does not exist, a placement with no pricing for the profile it is used
 * with — and those are cheap to catch before signing and expensive after.
 *
 * Pure functions over parsed YAML, no network and no CosmJS, so the intricate part
 * is pinned by tests against the SDL that actually ships in the examples repo.
 */

import { ALLOWLIST_CAPABILITY, normalizeAllowFrom } from './allowFrom'
import { parse as parseYaml, YAMLParseError } from 'yaml'

export type Severity = 'error' | 'warning'

export interface SdlIssue {
  severity: Severity
  /** Dotted path into the document, e.g. `deployment.web.dcloud.profile`. */
  path: string
  message: string
}

export interface Coin {
  denom: string
  amount: string
}

export interface SdlDocument {
  version?: unknown
  services?: Record<string, unknown>
  profiles?: { compute?: Record<string, unknown>; placement?: Record<string, unknown> }
  deployment?: Record<string, Record<string, unknown>>
}

/** Thrown only for input that is not YAML at all. Everything else is an issue. */
export class SdlParseError extends Error {
  readonly line: number | undefined
  constructor(message: string, line?: number) {
    super(message)
    this.name = 'SdlParseError'
    this.line = line
  }
}

export function parseSdl(text: string): SdlDocument {
  if (!text.trim()) throw new SdlParseError('The SDL is empty.')
  let doc: unknown
  try {
    doc = parseYaml(text)
  } catch (err) {
    if (err instanceof YAMLParseError) {
      throw new SdlParseError(err.message, err.linePos?.[0]?.line)
    }
    throw new SdlParseError(err instanceof Error ? err.message : String(err))
  }
  if (doc === null || typeof doc !== 'object' || Array.isArray(doc)) {
    throw new SdlParseError('The SDL must be a YAML mapping at the top level.')
  }
  return doc as SdlDocument
}

// ── size and cpu parsing ─────────────────────────────────────────────────────

const SUFFIXES: Record<string, number> = {
  // Binary suffixes are what the upstream Akash SDL uses; decimal ones are accepted because
  // Kubernetes does and people copy from there.
  Ki: 1024,
  Mi: 1024 ** 2,
  Gi: 1024 ** 3,
  Ti: 1024 ** 4,
  K: 1000,
  M: 1000 ** 2,
  G: 1000 ** 3,
  T: 1000 ** 4,
  k: 1000,
}

/** Bytes for an SDL size like `512Mi`, or null if unparseable. */
export function parseSize(value: unknown): number | null {
  if (typeof value === 'number') return Number.isFinite(value) ? value : null
  if (typeof value !== 'string') return null
  const m = /^\s*(\d+(?:\.\d+)?)\s*([A-Za-z]*)\s*$/.exec(value)
  if (!m) return null
  const n = Number(m[1])
  const suffix = m[2] ?? ''
  if (!suffix) return n
  const mult = SUFFIXES[suffix]
  return mult === undefined ? null : n * mult
}

/**
 * Millicores for an SDL cpu value.
 *
 * `0.5` and `"500m"` mean the same thing, and both appear in real SDL — the
 * examples repo uses `0.25`, Kubernetes-style manifests use `250m`.
 */
export function parseCpu(value: unknown): number | null {
  if (typeof value === 'number') return Number.isFinite(value) ? Math.round(value * 1000) : null
  if (typeof value !== 'string') return null
  const trimmed = value.trim()
  if (/^\d+m$/.test(trimmed)) return Number(trimmed.slice(0, -1))
  const n = Number(trimmed)
  return Number.isFinite(n) ? Math.round(n * 1000) : null
}

export function formatBytes(bytes: number): string {
  const units = ['B', 'Ki', 'Mi', 'Gi', 'Ti']
  let v = bytes
  let i = 0
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024
    i++
  }
  const rounded = v % 1 === 0 ? String(v) : v.toFixed(1)
  return `${rounded}${units[i]}`
}

// ── validation ───────────────────────────────────────────────────────────────

/**
 * `http_options` bounds, from `pkg.akt.dev/go/sdl` v0.1.1 (`v2.go`), the version
 * node and provider both pin. A zero value means "use the default" there; a
 * value above a limit makes the SDL parser refuse the whole document. The
 * provider (`operator/hostname`, `cluster/kube/client_ingress.go`) applies the
 * values it is given and imposes no lower limit of its own, so these stand.
 * `next_tries` and `next_timeout` are unbounded `uint32`s.
 */
export const HTTP_OPTION_LIMITS = {
  maxBodySize: 104857600,
  readTimeout: 60000,
  sendTimeout: 60000,
  uint32: 4294967295,
} as const

/** The `next_cases` Go accepts. `off` must stand alone. */
export const HTTP_NEXT_CASES = ['error', 'timeout', '500', '502', '503', '504', '403', '404', '429', 'off'] as const

const HTTP_OPTION_FIELDS: Record<string, number> = {
  max_body_size: HTTP_OPTION_LIMITS.maxBodySize,
  read_timeout: HTTP_OPTION_LIMITS.readTimeout,
  send_timeout: HTTP_OPTION_LIMITS.sendTimeout,
  next_tries: HTTP_OPTION_LIMITS.uint32,
  next_timeout: HTTP_OPTION_LIMITS.uint32,
}

/**
 * Check one expose entry's `http_options` the way the Go parser would.
 *
 * Numbers must be YAML integers: Go decodes into `uint32`, so `64Mi` or a quoted
 * number fails there. An unknown key is an error rather than dropped, because Go
 * would drop it silently and a misspelled `max_body_size` is the exact limit the
 * tenant meant to raise.
 */
function validateHttpOptions(raw: unknown, path: string, err: (path: string, message: string) => void) {
  if (!isRecord(raw)) {
    err(path, 'http_options must be a mapping.')
    return
  }
  for (const [key, value] of Object.entries(raw)) {
    const at = `${path}.${key}`
    if (key === 'next_cases') {
      if (!Array.isArray(value) || !value.every((c) => typeof c === 'string' || typeof c === 'number')) {
        err(at, 'next_cases must be a list.')
        continue
      }
      const cases = value.map(String)
      for (const c of cases) {
        if (!(HTTP_NEXT_CASES as readonly string[]).includes(c)) {
          err(at, `Unknown next case "${c}". Use one of ${HTTP_NEXT_CASES.join(', ')}.`)
        }
      }
      if (cases.includes('off') && cases.length !== 1) err(at, '"off" cannot be combined with other next cases.')
      continue
    }
    const limit = HTTP_OPTION_FIELDS[key]
    if (limit === undefined) {
      err(at, `Unknown http_options field "${key}".`)
      continue
    }
    if (typeof value !== 'number' || !Number.isInteger(value) || value < 0) {
      err(at, `${key} must be a whole number.`)
    } else if (value > limit) {
      err(at, `${key} cannot be greater than ${limit}.`)
    }
  }
}


function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v)
}

/** Parse a token amount without passing through a lossy IEEE-754 number. */
function parseCoinInteger(value: unknown): bigint | null {
  if (typeof value === 'bigint') return value >= 0n ? value : null
  if (typeof value === 'number') {
    return Number.isSafeInteger(value) && value >= 0 ? BigInt(value) : null
  }
  if (typeof value === 'string' && /^\d+$/.test(value.trim())) {
    try { return BigInt(value.trim()) } catch { return null }
  }
  return null
}

/**
 * Every problem worth reporting, in document order.
 *
 * Cross-reference errors come first in intent: they are the ones that pass a
 * YAML check, cost gas, and then fail with a message about a group index.
 */
export function validateSdl(doc: SdlDocument): SdlIssue[] {
  const issues: SdlIssue[] = []
  const err = (path: string, message: string) =>
    issues.push({ severity: 'error', path, message })
  const warn = (path: string, message: string) =>
    issues.push({ severity: 'warning', path, message })

  if (String(doc.version ?? '') !== '2.0') {
    err('version', `version must be "2.0" (found ${JSON.stringify(doc.version ?? null)}).`)
  }

  const services = isRecord(doc.services) ? doc.services : {}
  const compute = isRecord(doc.profiles?.compute) ? doc.profiles!.compute! : {}
  const placements = isRecord(doc.profiles?.placement) ? doc.profiles!.placement! : {}
  const deployment = isRecord(doc.deployment) ? doc.deployment : {}
  /** Services with a port restricted by allow_from; their placements must require an enforcing host. */
  const restrictedServices = new Set<string>()

  if (Object.keys(services).length === 0) err('services', 'At least one service is required.')
  if (Object.keys(deployment).length === 0) {
    err('deployment', 'At least one deployment entry is required.')
  }

  // services
  for (const [name, raw] of Object.entries(services)) {
    const svc = isRecord(raw) ? raw : {}
    if (!svc.image || typeof svc.image !== 'string') {
      err(`services.${name}.image`, 'An image is required.')
    } else if (/(^|:)latest$/.test(svc.image)) {
      // Not an error — but a lease can outlive the tag, and the workload that
      // restarts is then not the one that was deployed.
      warn(`services.${name}.image`, 'A `latest` tag is not pinned; the workload can change under you.')
    }
    if (svc.expose !== undefined && !Array.isArray(svc.expose)) {
      err(`services.${name}.expose`, 'expose must be a list.')
    }
    for (const [i, e] of (Array.isArray(svc.expose) ? svc.expose : []).entries()) {
      const exp = isRecord(e) ? e : {}
      const port = Number(exp.port)
      if (!Number.isInteger(port) || port < 1 || port > 65535) {
        err(`services.${name}.expose[${i}].port`, 'port must be an integer between 1 and 65535.')
      }
      const proto = exp.proto === undefined ? 'tcp' : String(exp.proto).toLowerCase()
      if (!['tcp', 'udp', 'http'].includes(proto)) {
        err(`services.${name}.expose[${i}].proto`, `proto must be tcp, udp or http (found ${proto}).`)
      }
      const targets = Array.isArray(exp.to) ? exp.to : []
      for (const [j, t] of targets.entries()) {
        const target = isRecord(t) ? t : {}
        if (typeof target.service === 'string' && !(target.service in services)) {
          err(
            `services.${name}.expose[${i}].to[${j}].service`,
            `No service named "${target.service}".`,
          )
        }
        if (target.allow_from !== undefined && target.allow_from !== null) {
          const path = `services.${name}.expose[${i}].to[${j}].allow_from`
          if (!Array.isArray(target.allow_from) || target.allow_from.some((entry) => typeof entry !== 'string')) {
            err(path, 'allow_from must be a list of IP addresses or ranges.')
          } else if (target.allow_from.length > 0) {
            if (target.global !== true) err(path, 'allow_from only applies to a port reachable from the internet (global: true).')
            // Same rule as the go fork's manifest validation and go-sdl: the
            // leased-IP path is not proven to keep the client address, and
            // provider v0.5.0 would refuse it after the lease exists.
            if (typeof target.ip === 'string' && target.ip !== '') err(path, 'allow_from is not supported on a leased-IP port yet.')
            restrictedServices.add(name)
            const result = normalizeAllowFrom(target.allow_from as string[])
            if (!result.ok) err(result.index === undefined ? path : `${path}[${result.index}]`, result.message)
          }
        }
        // Go parses `to[].http_options` and never reads it: only the expose
        // entry's own block reaches the manifest.
        if (target.http_options !== undefined) {
          warn(
            `services.${name}.expose[${i}].to[${j}].http_options`,
            'http_options here is ignored. Set it on the expose entry itself.',
          )
        }
      }
      if (exp.http_options !== undefined) {
        validateHttpOptions(exp.http_options, `services.${name}.expose[${i}].http_options`, err)
        // It reaches the manifest either way, but only a global port exposed
        // as 80 is served through the host's HTTP ingress, which applies it.
        const external = exp.as === undefined ? port : Number(exp.as)
        const global = targets.some((t) => isRecord(t) && t.global === true)
        if (external !== 80 || !global) {
          warn(
            `services.${name}.expose[${i}].http_options`,
            'http_options only applies to a global port exposed as 80. Here it has no effect.',
          )
        }
      }
    }
    if (svc.env !== undefined && !Array.isArray(svc.env)) {
      err(`services.${name}.env`, 'env must be a list of "KEY=value" strings.')
    }
    for (const [i, entry] of (Array.isArray(svc.env) ? svc.env : []).entries()) {
      if (typeof entry !== 'string' || !entry.includes('=')) {
        err(`services.${name}.env[${i}]`, 'Each env entry must be "KEY=value".')
      }
    }
  }

  // compute profiles
  for (const [name, raw] of Object.entries(compute)) {
    const profile = isRecord(raw) ? raw : {}
    const resources = isRecord(profile.resources) ? profile.resources : null
    if (!resources) {
      err(`profiles.compute.${name}.resources`, 'resources is required.')
      continue
    }
    const cpu = isRecord(resources.cpu) ? parseCpu(resources.cpu.units) : null
    if (cpu === null || cpu <= 0) {
      err(`profiles.compute.${name}.resources.cpu.units`, 'cpu units must be a positive number (e.g. 0.5 or "500m").')
    }
    const memory = isRecord(resources.memory) ? parseSize(resources.memory.size) : null
    if (memory === null || memory <= 0) {
      err(`profiles.compute.${name}.resources.memory.size`, 'memory size must be a size like "512Mi".')
    }
    // storage is either one entry or a list of named volumes.
    const storageEntries = Array.isArray(resources.storage)
      ? resources.storage
      : resources.storage === undefined
        ? []
        : [resources.storage]
    if (storageEntries.length === 0) {
      err(`profiles.compute.${name}.resources.storage`, 'storage is required.')
    }
    for (const [i, s] of storageEntries.entries()) {
      const entry = isRecord(s) ? s : {}
      const at = Array.isArray(resources.storage) ? `[${i}]` : ''
      if (parseSize(entry.size) === null) {
        err(`profiles.compute.${name}.resources.storage${at}.size`, 'storage size must be a size like "1Gi".')
      }
    }

    // ── Features the pipeline cannot represent ─────────────────────────────
    // An unrepresented feature is not a rejected deployment — it is a *downgraded*
    // one: the chain accepts the MsgCreateDeployment, the tenant funds escrow, wins a
    // lease, and gets a machine they did not ask for, with nothing pointing at the
    // conversion. Same asymmetry the gas comment in fees.ts turns on: a refused
    // deployment is recoverable, a silently different one is not. So these are errors
    // rather than warnings, and they live here because this is the last layer that
    // still has the tenant's own words to point at.
    //
    // Storage attributes are NOT in this list any more — they are carried through to
    // the encoder and asserted byte-for-byte against the Go marshaller, because the
    // console ships a template whose entire purpose is a volume that survives a
    // restart, and `persistent: true` is what does that.
    //
    // GPU is still refused, and the difference is not arbitrary: storage attributes
    // are a flat, sorted key/value list identical in shape to placement attributes,
    // while `gpu.attributes` is nested (`vendor: {nvidia: [{model: …}]}`) and
    // `gpu.units` has no field in `ResourceBlock` at all. Carrying GPUs is real work
    // with its own vectors, and no shipped template needs it.
    const gpu = isRecord(resources.gpu) ? resources.gpu : null
    if (gpu) {
      const units = parseCpu(gpu.units)
      if (units !== null && units > 0) {
        err(
          `profiles.compute.${name}.resources.gpu.units`,
          'This console cannot deploy GPUs yet. It would encode gpu.units as 0 and ' +
            'the chain would accept it, so the lease you paid for would have no GPU. ' +
            'Remove the gpu block, or use the CLI.',
        )
      }
      if (gpu.attributes !== undefined) {
        err(
          `profiles.compute.${name}.resources.gpu.attributes`,
          'This console cannot deploy GPU attributes (vendor, model) yet, and would ' +
            'drop them silently. Remove them, or use the CLI.',
        )
      }
    }
    for (const kind of ['cpu', 'memory'] as const) {
      const block = isRecord(resources[kind]) ? resources[kind] : null
      if (block && block.attributes !== undefined) {
        err(
          `profiles.compute.${name}.resources.${kind}.attributes`,
          `This console cannot deploy ${kind} attributes yet, and would drop them ` +
            'silently. Remove them, or use the CLI.',
        )
      }
    }
  }

  // placements
  for (const [name, raw] of Object.entries(placements)) {
    const placement = isRecord(raw) ? raw : {}
    if (placement.pricing !== undefined && !isRecord(placement.pricing)) {
      err(`profiles.placement.${name}.pricing`, 'pricing must be a mapping of profile name to price.')
    }
    for (const [profileName, p] of Object.entries(
      isRecord(placement.pricing) ? placement.pricing : {},
    )) {
      const price = isRecord(p) ? p : {}
      if (!price.denom || typeof price.denom !== 'string') {
        err(`profiles.placement.${name}.pricing.${profileName}.denom`, 'denom is required.')
      }
      const amount = parseCoinInteger(price.amount)
      if (amount === null || amount <= 0n) {
        err(`profiles.placement.${name}.pricing.${profileName}.amount`, 'amount must be a positive integer.')
      }
      if (!(profileName in compute)) {
        err(
          `profiles.placement.${name}.pricing.${profileName}`,
          `Priced profile "${profileName}" is not defined under profiles.compute.`,
        )
      }
    }
  }

  // deployment — the cross-references that cost gas to discover
  for (const [serviceName, raw] of Object.entries(deployment)) {
    if (!(serviceName in services)) {
      err(`deployment.${serviceName}`, `No service named "${serviceName}".`)
    }
    const byPlacement = isRecord(raw) ? raw : {}
    if (Object.keys(byPlacement).length === 0) {
      err(`deployment.${serviceName}`, 'At least one placement is required.')
    }
    for (const [placementName, e] of Object.entries(byPlacement)) {
      const entry = isRecord(e) ? e : {}
      const base = `deployment.${serviceName}.${placementName}`
      if (!(placementName in placements)) {
        err(base, `No placement named "${placementName}" under profiles.placement.`)
      }
      if (restrictedServices.has(serviceName) && placementName in placements) {
        // Without the attribute any host can take the lease, including one
        // that would run the port open (an older provider under
        // --contract-manifest-bypass). The guided path always adds it.
        const attributes = isRecord(placements[placementName]) ? (placements[placementName] as Record<string, unknown>).attributes : undefined
        if (!isRecord(attributes) || String(attributes[ALLOWLIST_CAPABILITY]) !== 'true') {
          err(
            `profiles.placement.${placementName}.attributes`,
            `Service "${serviceName}" restricts a port with allow_from, so placement "${placementName}" must require ${ALLOWLIST_CAPABILITY}: "true" — otherwise a host that ignores the list can take the lease.`,
          )
        }
      }
      const profileName = typeof entry.profile === 'string' ? entry.profile : ''
      if (!profileName) {
        err(`${base}.profile`, 'profile is required.')
      } else if (!(profileName in compute)) {
        err(`${base}.profile`, `No compute profile named "${profileName}".`)
      } else {
        const pricing = isRecord(placements[placementName])
          ? (placements[placementName] as Record<string, unknown>).pricing
          : undefined
        if (isRecord(pricing) && !(profileName in pricing)) {
          // The mistake that reaches the chain and comes back as a group error.
          err(
            `${base}.profile`,
            `Placement "${placementName}" has no pricing for profile "${profileName}".`,
          )
        }
      }
      const count = Number(entry.count)
      if (!Number.isInteger(count) || count < 1) {
        err(`${base}.count`, 'count must be an integer of 1 or more.')
      }
    }
  }

  // Unused definitions are warnings: harmless, usually a leftover, occasionally
  // a sign the author edited the wrong name.
  const usedProfiles = new Set<string>()
  const usedPlacements = new Set<string>()
  for (const byPlacement of Object.values(deployment)) {
    for (const [placementName, e] of Object.entries(isRecord(byPlacement) ? byPlacement : {})) {
      usedPlacements.add(placementName)
      if (isRecord(e) && typeof e.profile === 'string') usedProfiles.add(e.profile)
    }
  }
  for (const name of Object.keys(compute)) {
    if (!usedProfiles.has(name)) warn(`profiles.compute.${name}`, 'This profile is never used.')
  }
  for (const name of Object.keys(placements)) {
    if (!usedPlacements.has(name)) warn(`profiles.placement.${name}`, 'This placement is never used.')
  }
  for (const name of Object.keys(services)) {
    if (!(name in deployment)) warn(`services.${name}`, 'This service is never deployed.')
  }

  return issues
}

// ── summary and price ceiling ────────────────────────────────────────────────

export interface ResourceTotals {
  cpuMillis: number
  memoryBytes: number
  storageBytes: number
  replicas: number
}

export interface PriceCeiling {
  denom: string
  /** Sum over every placement entry of count × price. */
  perBlock: bigint
  perDay: bigint
  perMonth: bigint
  /** True when placements price in more than one denomination, which cannot be
   *  summed — the caller must show them separately rather than a wrong total. */
  mixedDenoms: boolean
}

import { BLOCK_SECONDS } from './pricing'

export { BLOCK_SECONDS }


export function summarize(doc: SdlDocument): ResourceTotals {
  const compute = isRecord(doc.profiles?.compute) ? doc.profiles!.compute! : {}
  const deployment = isRecord(doc.deployment) ? doc.deployment : {}
  const totals: ResourceTotals = { cpuMillis: 0, memoryBytes: 0, storageBytes: 0, replicas: 0 }

  for (const byPlacement of Object.values(deployment)) {
    for (const e of Object.values(isRecord(byPlacement) ? byPlacement : {})) {
      const entry = isRecord(e) ? e : {}
      const count = Number.isInteger(Number(entry.count)) ? Number(entry.count) : 0
      const profile = isRecord(compute[String(entry.profile)])
        ? (compute[String(entry.profile)] as Record<string, unknown>)
        : null
      const resources = profile && isRecord(profile.resources) ? profile.resources : null
      if (!resources) continue

      totals.replicas += count
      const cpu = isRecord(resources.cpu) ? parseCpu(resources.cpu.units) : null
      if (cpu) totals.cpuMillis += cpu * count
      const mem = isRecord(resources.memory) ? parseSize(resources.memory.size) : null
      if (mem) totals.memoryBytes += mem * count
      const storageEntries = Array.isArray(resources.storage)
        ? resources.storage
        : resources.storage === undefined
          ? []
          : [resources.storage]
      for (const s of storageEntries) {
        const size = isRecord(s) ? parseSize(s.size) : null
        if (size) totals.storageBytes += size * count
      }
    }
  }
  return totals
}

/**
 * The maximum the tenant can be charged, not an estimate of what they will pay.
 *
 * SDL pricing is a *ceiling*: providers bid at or below it, and the lease is struck
 * at the winning bid. Calling this an "estimated cost" would overstate it every
 * time, so the field names and the UI say ceiling.
 *
 * Integer arithmetic throughout — a per-month figure is per-block × 14,400 × 30,
 * which leaves the range where a float is safe surprisingly quickly.
 */
export function priceCeiling(doc: SdlDocument, blockSeconds = BLOCK_SECONDS): PriceCeiling {
  const placements = isRecord(doc.profiles?.placement) ? doc.profiles!.placement! : {}
  const deployment = isRecord(doc.deployment) ? doc.deployment : {}

  let perBlock = 0n
  const denoms = new Set<string>()

  for (const byPlacement of Object.values(deployment)) {
    for (const [placementName, e] of Object.entries(isRecord(byPlacement) ? byPlacement : {})) {
      const entry = isRecord(e) ? e : {}
      const placement = isRecord(placements[placementName])
        ? (placements[placementName] as Record<string, unknown>)
        : null
      const pricing = placement && isRecord(placement.pricing) ? placement.pricing : null
      const price = pricing && isRecord(pricing[String(entry.profile)])
        ? (pricing[String(entry.profile)] as Record<string, unknown>)
        : null
      if (!price) continue

      const amount = parseCoinInteger(price.amount)
      const count = Number(entry.count)
      if (amount === null || !Number.isInteger(count) || count < 1) continue
      denoms.add(String(price.denom ?? ''))
      perBlock += amount * BigInt(count)
    }
  }

  const perDay = (perBlock * BigInt(Math.round(86_400 / blockSeconds)))
  return {
    denom: denoms.size === 1 ? [...denoms][0]! : '',
    perBlock,
    perDay,
    perMonth: perDay * 30n,
    mixedDenoms: denoms.size > 1,
  }
}
